import React, { useState } from 'react';
import {
  ReleaseV192,
  ReleaseV191,
  ReleaseV180,
  ReleaseV170,
  ReleaseV164,
  ReleaseV163,
  ReleaseV161,
  ReleaseV160,
  ReleaseV150,
  ReleaseV140,
  ReleaseV130,
  ReleaseV120,
  ReleaseV110,
  ReleaseV100,
} from './releases';

export default function ReleasesSection() {
  const [selectedImage, setSelectedImage] = useState(null);

  const openModal = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  // Helper function to generate clickable list items
  const createClickableListItem = (text, imagePath) => (
    <li>• <span
      className="text-purple-300 hover:text-purple-200 cursor-pointer underline hover:no-underline transition-all"
      onClick={() => openModal(`/images${imagePath}`)}
    >
      {text}
    </span></li>
  );

  // Helper function to generate thumbnail elements
  const createThumbnail = (imagePath, altText, otherText) => (
    <div
      className="inline-block cursor-pointer hover:opacity-80 transition-opacity"
      onClick={() => openModal(`/images${imagePath}`)}
    >
      <img
        src={`/images${imagePath}`}
        alt={altText}
        className="w-32 h-20 object-cover rounded-lg border border-purple-500/30 hover:border-purple-500/60 transition-colors"
      />
      {otherText === undefined ? <p className="text-xs text-gray-400 mt-1 text-center">Click to enlarge</p> : <p className="text-xs text-gray-400 mt-1 text-center">{otherText}</p>}
    </div>
  );

  const helpers = { createClickableListItem, createThumbnail };

  const releaseAnchorId = (version) => version.replace(/\./g, '-');

  const scrollToRelease = (version) => {
    const el = document.getElementById(releaseAnchorId(version));
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** Sidebar dates: dd/Mmm/yyyy (matches release cards; update when a release date changes). */
  const releases = [
    [ReleaseV192, 'v1.9.2', '14/Apr/2026'],
    [ReleaseV191, 'v1.9.1', '15/Feb/2026'],
    [ReleaseV180, 'v1.8.0', '08/Dec/2025'],
    [ReleaseV170, 'v1.7.0', '21/Oct/2025'],
    [ReleaseV164, 'v1.6.4', '15/Oct/2025'],
    [ReleaseV163, 'v1.6.3', '01/Oct/2025'],
    [ReleaseV161, 'v1.6.1', '18/Sep/2025'],
    [ReleaseV160, 'v1.6.0', '29/Aug/2025'],
    [ReleaseV150, 'v1.5.0', '05/Aug/2025'],
    [ReleaseV140, 'v1.4.0', '29/Jun/2025'],
    [ReleaseV130, 'v1.3.0', '08/Jun/2025'],
    [ReleaseV120, 'v1.2.0', '03/Jun/2025'],
    [ReleaseV110, 'v1.1.0', '31/Jan/2025'],
    [ReleaseV100, 'v1.0.0', '14/Jan/2024'],
  ];

  return (
    <div className="py-20 bg-black/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 lg:mb-16">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">Release Notes</h2>
          <p className="text-xl text-gray-300">Stay updated with the latest Poppy Poker features</p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          <aside className="w-full shrink-0 lg:w-60 lg:sticky lg:top-24 lg:self-start z-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-purple-400/90 mb-2 lg:mb-3">Versions</p>
            <nav
              aria-label="Release versions"
              className="lg:hidden flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:thin]"
            >
            {releases.map(([_, version, navDate]) => (
              <button
                key={version}
                type="button"
                onClick={() => scrollToRelease(version)}
                className="flex shrink-0 items-center gap-2 rounded-lg border border-purple-500/30 bg-black/50 px-3 py-2 text-left text-sm font-medium text-purple-300 transition-colors hover:border-purple-400/60 hover:bg-purple-500/10 hover:text-purple-200"
              >
                <span className="leading-tight">{version}</span>
                <span className="text-xs font-normal text-gray-500 tabular-nums">{navDate}</span>
              </button>
            ))}
            </nav>
            <nav
              aria-label="Release versions"
              className="hidden lg:block rounded-xl border border-purple-500/30 bg-black/40 p-3 shadow-lg"
            >
              <ul className="space-y-0.5">
                {releases.map(([_, version, navDate]) => (
                  <li key={version}>
                    <button
                      type="button"
                      onClick={() => scrollToRelease(version)}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-400 transition-colors hover:bg-purple-500/15 hover:text-purple-300"
                    >
                      <span className="font-medium text-gray-300">{version}</span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-500">{navDate}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <div className="min-w-0 flex-1 space-y-8">
            {releases.map(([ReleaseComponent, version]) => (
              <div
                key={version}
                id={releaseAnchorId(version)}
                className="scroll-mt-28"
              >
                <ReleaseComponent {...helpers} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-2"
          onClick={closeModal}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Full size preview"
              className="max-w-full max-h-full w-auto h-auto object-contain"
              style={{ maxWidth: '100vw', maxHeight: '100vh' }}
            />
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold bg-black/70 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/90 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
